import { useEffect, useState } from 'react';

/**
 * Transforms the raw master-scope API response into the
 * Region → Country → Unit tree shape expected by TreeView.
 */
export function transformMasterScopeResponse(regions) {
  return regions.map((region) => ({
    ...region,
    code: region.regionCode,
    name: 'Region ' + region.regionCode,
    children: region.countries.map((country) => {
      const units = country.units.map((unit) => ({
        code: unit.code,
        name: unit.name + ' - ' + unit.code
      }));
      return {
        ...country,
        code: country.code,
        name: country.name + ' (' + country.code + ')',
        children: units
      };
    })
  }));
}

/**
 * Walks a checked Region → Country → Unit tree (as returned by TreeView via
 * setParentMasterData) and returns an array of selected unit codes.
 * - If a Region is checked  → collect ALL unit codes under it
 * - If a Country is checked → collect ALL unit codes under it
 * - Otherwise              → collect only individually-checked unit codes
 */
export function buildUnitCodesFromMasterData(data) {
  let unitList = [];
  data.forEach((region) => {
    const regionCountries = Array.isArray(region.children)
      ? region.children
      : [];
    if (region.checked) {
      regionCountries.forEach((country) => {
        const units = Array.isArray(country.children) ? country.children : [];
        units.forEach((unit) => unitList.push(unit.code));
      });
    } else {
      regionCountries.forEach((country) => {
        if (country.checked) {
          const units = Array.isArray(country.children) ? country.children : [];
          units.forEach((unit) => unitList.push(unit.code));
        } else {
          const countryUnits = Array.isArray(country.children)
            ? country.children
            : [];
          countryUnits.forEach((unit) => {
            if (unit.checked) unitList.push(unit.code);
          });
        }
      });
    }
  });
  return unitList;
}

/**
 * Shared hook used by ScopeFilter and ScopeQuickFilter.
 * Manages the clearAll flag and automatically sets it when selectedUnits
 * becomes empty, and resets it when selectedUnits becomes non-empty.
 *
 * @param {string[] | null | undefined} selectedUnits
 * @returns {[boolean, Function]} [clearAll, setClearAll]
 */
export function useScopeClearAll(selectedUnits) {
  const [clearAll, setClearAll] = useState(
    !selectedUnits || selectedUnits.length === 0
  );

  useEffect(() => {
    if (!selectedUnits || selectedUnits.length === 0) {
      setClearAll(true);
    } else {
      setClearAll(false);
    }
  }, [selectedUnits]);

  return [clearAll, setClearAll];
}
